import { Capacitor } from '@capacitor/core';
import { readSecureItem, removeSecureItem, writeSecureItem } from '@/lib/secureStorage';
import { doNothing } from '@/utils/doNothing';
import { getBaseUrl, getPlatform, type Platform } from './config';

export type AuthClient = Platform;

export interface SessionPayload {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: string | number | null;
  aal?: string;
}

interface NativeSession {
  accessToken: string;
  refreshToken: string;
}

const NATIVE_SESSION_KEY = 'khova.auth.session';
// Web cannot see its httpOnly cookies, so this marker is what tells the API client a session
// exists and a 401 is worth one refresh. A signed-out visitor never has it, so its 401s are final.
const SESSION_KIND_KEY = 'khova.auth.session-kind';
const KHOVA_SESSION_KIND = 'khova';

let nativeSession: NativeSession | null | undefined;
let nativeSessionLoad: Promise<NativeSession | null> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
// Bumped by every sign-in and sign-out. A refresh that started under an older generation lost
// the race (the user signed out or in meanwhile): its answer must neither resurrect the old
// session nor clear the new one.
let sessionGeneration = 0;

// 400/401 reject the refresh token itself; a 403 only ends the session for these account
// states (contract §4). Anything else (5xx, 429, another 403) is not a verdict on the session.
const SESSION_ENDING_FORBIDDEN_CODES = ['user-deactivated', 'account-suspended'];

export const getAuthClient = (): AuthClient => getPlatform();

const isNative = (): boolean => Capacitor.isNativePlatform();

const parseNativeSession = (raw: string | null): NativeSession | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<NativeSession>;
    return parsed.accessToken && parsed.refreshToken
      ? { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken }
      : null;
  } catch {
    return null;
  }
};

const loadNativeSession = async (): Promise<NativeSession | null> => {
  if (!isNative()) {
    return null;
  }
  if (nativeSession !== undefined) {
    return nativeSession;
  }
  if (!nativeSessionLoad) {
    nativeSessionLoad = readSecureItem(NATIVE_SESSION_KEY)
      .then(parseNativeSession)
      .catch(() => null)
      .then((session) => {
        // A save that landed while the read was pending is newer than what was read.
        if (nativeSession === undefined) {
          nativeSession = session;
        }
        nativeSessionLoad = null;
        return nativeSession;
      });
  }
  return nativeSessionLoad;
};

const writeSessionKind = (isKhova: boolean): void => {
  try {
    if (isKhova) {
      window.localStorage.setItem(SESSION_KIND_KEY, KHOVA_SESSION_KIND);
      return;
    }
    window.localStorage.removeItem(SESSION_KIND_KEY);
  } catch {
    // Storage can be unavailable (private mode). Losing the marker only costs the silent
    // refresh: the user signs in again.
  }
};

export const hasKhovaSession = (): boolean => {
  try {
    return window.localStorage.getItem(SESSION_KIND_KEY) === KHOVA_SESSION_KIND;
  } catch {
    return false;
  }
};

export const markKhovaSession = (): void => writeSessionKind(true);

export const getAccessToken = async (): Promise<string | null> => (await loadNativeSession())?.accessToken ?? null;

export const getRefreshToken = async (): Promise<string | null> => (await loadNativeSession())?.refreshToken ?? null;

const persistSession = async (session: SessionPayload | null | undefined): Promise<void> => {
  markKhovaSession();
  if (!isNative() || !session?.accessToken || !session.refreshToken) {
    return;
  }
  nativeSession = { accessToken: session.accessToken, refreshToken: session.refreshToken };
  await writeSecureItem(NATIVE_SESSION_KEY, JSON.stringify(nativeSession)).catch(doNothing);
};

/** Persists a session returned by login, reset or invite accept. */
export const saveSession = async (session: SessionPayload | null | undefined): Promise<void> => {
  sessionGeneration += 1;
  await persistSession(session);
};

const removeSession = async (): Promise<void> => {
  writeSessionKind(false);
  if (!isNative()) {
    return;
  }
  nativeSession = null;
  await removeSecureItem(NATIVE_SESSION_KEY).catch(doNothing);
};

export const clearSession = async (): Promise<void> => {
  sessionGeneration += 1;
  await removeSession();
};

const readErrorCode = async (res: Response): Promise<unknown> => {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown; data?: { error?: unknown } };
  return body.error ?? body.data?.error;
};

const isSessionRejected = async (res: Response): Promise<boolean> => {
  if (res.status === 400 || res.status === 401) {
    return true;
  }
  if (res.status !== 403) {
    return false;
  }
  const code = await readErrorCode(res);
  return typeof code === 'string' && SESSION_ENDING_FORBIDDEN_CODES.includes(code);
};

const requestRefresh = async (): Promise<boolean> => {
  const generation = sessionGeneration;
  const refreshToken = await getRefreshToken();
  if (isNative() && !refreshToken) {
    return false;
  }
  const isCurrentSession = () => generation === sessionGeneration;
  try {
    const res = await fetch(`${getBaseUrl()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ client: getAuthClient(), ...(refreshToken && { refreshToken }) }),
    });
    if (!res.ok) {
      // Only a verdict on this very session ends it; a transient failure keeps the tokens.
      if ((await isSessionRejected(res)) && isCurrentSession()) {
        await removeSession();
      }
      return false;
    }
    const body = (await res.json().catch(() => ({}))) as { data?: { session?: SessionPayload } };
    if (!isCurrentSession()) {
      return false;
    }
    await persistSession(body.data?.session);
    return true;
  } catch {
    // A network failure is not a rejected session: keep the tokens for the next attempt.
    return false;
  }
};

/** Resolves once no refresh is in flight, whatever its outcome. */
export const waitForRefresh = async (): Promise<void> => {
  await refreshInFlight?.catch(doNothing);
};

/**
 * Refreshes the Khova session once for every caller that saw a 401 at the same time
 * (single flight), so parallel requests never burn the one-time refresh token twice.
 */
export const refreshSession = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = requestRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/** Test-only: forgets the in-memory native session so the next read goes back to storage. */
export const resetSessionCacheForTests = (): void => {
  nativeSession = undefined;
  nativeSessionLoad = null;
  refreshInFlight = null;
  sessionGeneration = 0;
};
